import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import './css/LogoLoop.css'

const ANIMATION_CONFIG = {
  SMOOTH_TAU: 0.25,
  MIN_COPIES: 2,
  COPY_HEADROOM: 2
}

const toCssLength = value =>
  typeof value === 'number' ? `${value}px` : value ?? undefined

const useAnimationLoop = (
  trackRef,
  targetVelocity,
  seqWidth,
  isHovered,
  pauseOnHover
) => {
  const rafRef = useRef(null)
  const lastTimestampRef = useRef(null)
  const offsetRef = useRef(0)
  const velocityRef = useRef(0)

  useEffect(() => {
    const track = trackRef.current
    if (!track) return undefined

    if (seqWidth > 0) {
      offsetRef.current = ((offsetRef.current % seqWidth) + seqWidth) % seqWidth
      track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`
    }

    const animate = timestamp => {
      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = timestamp
      }

      const deltaTime = Math.max(0, timestamp - lastTimestampRef.current) / 1000
      lastTimestampRef.current = timestamp

      const target = pauseOnHover && isHovered ? 0 : targetVelocity
      const easingFactor = 1 - Math.exp(-deltaTime / ANIMATION_CONFIG.SMOOTH_TAU)
      velocityRef.current += (target - velocityRef.current) * easingFactor

      if (seqWidth > 0) {
        let nextOffset = offsetRef.current + velocityRef.current * deltaTime
        nextOffset = ((nextOffset % seqWidth) + seqWidth) % seqWidth
        offsetRef.current = nextOffset
        track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`
      }

      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      lastTimestampRef.current = null
    }
  }, [trackRef, targetVelocity, seqWidth, isHovered, pauseOnHover])
}

function renderLogoContent(item) {
  if ('node' in item) {
    return (
      <span className="logoloop__node" aria-hidden={!!item.href && !item.ariaLabel}>
        {item.node}
      </span>
    )
  }

  return (
    <img
      src={item.src}
      srcSet={item.srcSet}
      sizes={item.sizes}
      width={item.width}
      height={item.height}
      alt={item.alt ?? ''}
      title={item.title}
      loading="lazy"
      decoding="async"
      draggable={false}
    />
  )
}

function buildLogoItemContent(item) {
  const isNodeItem = 'node' in item
  const content = renderLogoContent(item)
  const ariaLabel = isNodeItem ? item.ariaLabel ?? item.title : item.alt ?? item.title

  if (item.href) {
    return (
      <a
        className="logoloop__link"
        href={item.href}
        aria-label={ariaLabel || 'logo link'}
        target="_blank"
        rel="noreferrer noopener"
      >
        {content}
      </a>
    )
  }

  return content
}

const LogoLoop = ({
  logos,
  speed = 120,
  direction = 'left',
  width = '100%',
  logoHeight = 28,
  gap = 32,
  pauseOnHover = true,
  fadeOut = false,
  fadeOutColor,
  scaleOnHover = false,
  ariaLabel = 'Partner logos',
  className,
  style
}) => {
  const containerRef = useRef(null)
  const trackRef = useRef(null)
  const seqRef = useRef(null)

  const [seqWidth, setSeqWidth] = useState(0)
  const [copyCount, setCopyCount] = useState(ANIMATION_CONFIG.MIN_COPIES)
  const [isHovered, setIsHovered] = useState(false)

  const targetVelocity = useMemo(() => {
    const magnitude = Math.abs(speed)
    const directionMultiplier = direction === 'left' ? 1 : -1
    const speedMultiplier = speed < 0 ? -1 : 1
    return magnitude * directionMultiplier * speedMultiplier
  }, [speed, direction])

  const updateDimensions = useCallback(() => {
    const containerWidth = containerRef.current?.clientWidth ?? 0
    const sequenceWidth = seqRef.current?.getBoundingClientRect?.()?.width ?? 0

    if (sequenceWidth > 0) {
      setSeqWidth(Math.ceil(sequenceWidth))
      const copiesNeeded =
        Math.ceil(containerWidth / sequenceWidth) + ANIMATION_CONFIG.COPY_HEADROOM
      setCopyCount(Math.max(ANIMATION_CONFIG.MIN_COPIES, copiesNeeded))
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const elements = [containerRef.current, seqRef.current].filter(Boolean)

    if (elements.length === 0) {
      updateDimensions()
      return undefined
    }

    if (!window.ResizeObserver) {
      const handleResize = () => updateDimensions()
      window.addEventListener('resize', handleResize)
      updateDimensions()
      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }

    const observer = new ResizeObserver(() => updateDimensions())
    elements.forEach(el => observer.observe(el))
    updateDimensions()

    return () => {
      elements.forEach(el => observer.unobserve(el))
      observer.disconnect()
    }
  }, [containerRef, seqRef, updateDimensions, logos, gap, logoHeight])

  useEffect(() => {
    const list = seqRef.current
    if (!list) {
      updateDimensions()
      return undefined
    }

    const images = Array.from(list.querySelectorAll('img'))
    if (images.length === 0) {
      updateDimensions()
      return undefined
    }

    let remaining = images.length
    const handleLoad = () => {
      remaining -= 1
      if (remaining <= 0) {
        updateDimensions()
      }
    }

    images.forEach(img => {
      if (img.complete) {
        handleLoad()
      } else {
        img.addEventListener('load', handleLoad, { once: true })
        img.addEventListener('error', handleLoad, { once: true })
      }
    })

    return () => {
      images.forEach(img => {
        img.removeEventListener('load', handleLoad)
        img.removeEventListener('error', handleLoad)
      })
    }
  }, [seqRef, logos, gap, logoHeight, updateDimensions])

  useAnimationLoop(trackRef, targetVelocity, seqWidth, isHovered, pauseOnHover)

  const cssVariables = useMemo(
    () => ({
      '--logoloop-gap': `${gap}px`,
      '--logoloop-logoHeight': `${logoHeight}px`,
      ...(fadeOutColor && { '--logoloop-fadeColor': fadeOutColor })
    }),
    [gap, logoHeight, fadeOutColor]
  )

  const rootClassName = useMemo(
    () =>
      ['logoloop', fadeOut && 'logoloop--fade', scaleOnHover && 'logoloop--scale-hover', className]
        .filter(Boolean)
        .join(' '),
    [fadeOut, scaleOnHover, className]
  )

  const handleMouseEnter = useCallback(() => {
    if (pauseOnHover) setIsHovered(true)
  }, [pauseOnHover])

  const handleMouseLeave = useCallback(() => {
    if (pauseOnHover) setIsHovered(false)
  }, [pauseOnHover])

  const renderLogoItem = useCallback(
    (item, key) => (
      <li className="logoloop__item" key={key}>
        {buildLogoItemContent(item)}
      </li>
    ),
    []
  )

  const logoLists = useMemo(
    () =>
      Array.from({ length: copyCount }, (_, copyIndex) => (
        <ul
          className="logoloop__list"
          key={`copy-${copyIndex}`}
          aria-hidden={copyIndex > 0}
          ref={copyIndex === 0 ? seqRef : undefined}
        >
          {logos.map((item, itemIndex) => renderLogoItem(item, `${copyIndex}-${itemIndex}`))}
        </ul>
      )),
    [copyCount, logos, renderLogoItem]
  )

  const containerStyle = useMemo(
    () => ({
      width: toCssLength(width) ?? '100%',
      ...cssVariables,
      ...style
    }),
    [width, cssVariables, style]
  )

  return (
    <div
      ref={containerRef}
      className={rootClassName}
      style={containerStyle}
      role="region"
      aria-label={ariaLabel}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="logoloop__track" ref={trackRef}>
        {logoLists}
      </div>
    </div>
  )
}

LogoLoop.displayName = 'LogoLoop'

export default React.memo(LogoLoop)
